import React, { useState, useMemo, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, Outlet, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { supabase } from './lib/supabaseClient';

import { type FilterState } from './features/orders';
import { WorkOrder, UserRole, Technician, Client, CompanySettings, WorkOrderStatus } from './types';
import { generateServiceReport, generateSummaryReport } from './utils/pdfHelper';
import { useAuth } from './contexts/AuthContext';
import { useToast } from './contexts/ToastContext';
import { isNetworkError } from './utils/network';
import { useConfirm } from './contexts/ConfirmContext';
import { useDashboardFilters } from './hooks/useDashboardFilters';
import { useOrderHandlers } from './features/orders';
// V2148: Robust Lazy Loading with auto-retry on Chunk/MIME errors
// FIX: Use sessionStorage to prevent infinite reload loop on persistent errors
const lazyRetry = (componentImport: () => Promise<any>) => {
    return React.lazy(async () => {
        const retryKey = 'chunk_reload_attempted';
        try {
            const result = await componentImport();
            // Clear the flag on success so future real errors can still retry once
            sessionStorage.removeItem(retryKey);
            return result;
        } catch (error: any) {
            logger.error("Chunk load error detected, attempting reload", error);
            const alreadyRetried = sessionStorage.getItem(retryKey);
            if (!alreadyRetried && typeof window !== 'undefined') {
                sessionStorage.setItem(retryKey, 'true');
                window.location.reload();
                // Return a never-resolving promise to suspend React while reloading
                return new Promise(() => { });
            }
            // If we already retried and still failing, throw to trigger ErrorBoundary
            throw error;
        }
    });
};

// Lazy loaded to reduce bundle size
const TechnicianManagement = lazyRetry(() => import('./features/technicians/components/TechnicianManagement').then(m => ({ default: m.TechnicianManagement })));
import TechnicianErrorBoundary from './features/technicians/components/TechnicianErrorBoundary';
const ClientManagement = lazyRetry(() => import('./features/clients').then(m => ({ default: m.ClientManagement })));
const ClientModal = lazyRetry(() => import('./features/clients').then(m => ({ default: m.ClientModal })));
import { InvoiceModal } from './components/InvoiceModal';
import { useAppHandlers } from './hooks/useAppHandlers';
import { logger } from './utils/logger';
import { PerformanceMonitor } from './utils/performance';

// Removed duplicate lazyRetry definition here
const Dashboard = lazyRetry(() => import('./features/dashboard'));
const WorkOrderList = lazyRetry(() => import('./features/orders'));
const ReportsView = lazyRetry(() => import('./features/reports').then(m => ({ default: m.ReportsView })));
const TechnicianView = lazyRetry(() => import('./features/technicians'));
const SettingsView = lazyRetry(() => import('./features/settings'));
const AccountView = lazyRetry(() => import('./features/account'));
const OnboardingFlow = lazyRetry(() => import('./features/onboarding').then(m => ({ default: m.OnboardingFlow })));
const JoinCompany = lazyRetry(() => import('./features/onboarding').then(m => ({ default: m.JoinCompany })));
const SelectPlan = lazyRetry(() => import('./features/onboarding').then(m => ({ default: m.SelectPlan })));
const InviteTechnicians = lazyRetry(() => import('./features/onboarding').then(m => ({ default: m.InviteTechnicians })));
const PricingPage = lazyRetry(() => import('./features/billing/components/PricingPage'));
const SubscriptionView = lazyRetry(() => import('./features/billing/components/SubscriptionView'));
const Login = lazyRetry(() => import('./features/auth').then(m => ({ default: m.default })));
const ResetPassword = lazyRetry(() => import('./features/auth').then(m => ({ default: m.ResetPassword })));
const Verified = lazyRetry(() => import('./features/auth').then(m => ({ default: m.Verified })));

// Eagerly prefetch LandingPage to avoid network waterfall, but use lazy to avoid CPU blocking (TBT)
const prefetchLandingPage = () => import('./features/public/LandingPage');
prefetchLandingPage().catch(() => {}); // Start download immediately in background
const LandingPage = lazyRetry(prefetchLandingPage);

const PrivacyPolicy = lazyRetry(() => import('./features/public/PrivacyPolicy'));
const TermsAndConditions = lazyRetry(() => import('./features/public/TermsAndConditions').then(m => ({ default: m.default })));
const ProtectedLayout = lazyRetry(() => import('./components/ProtectedLayout').then(m => ({ default: m.ProtectedLayout })));
const TechViewWrapper = lazyRetry(() => import('./components/TechViewWrapper').then(m => ({ default: m.TechViewWrapper })));


import { useOrdersDomain } from '@/hooks/data/useOrdersDomain';
import { useClientsDomain } from '@/hooks/data/useClientsDomain';
import { useTechniciansDomain } from '@/hooks/data/useTechniciansDomain';
import { useSettingsDomain } from '@/hooks/data/useSettingsDomain';
import { useDashboardStatsDomain } from '@/hooks/data/useDashboardStatsDomain';

const App: React.FC = () => {
    const { isAuthenticated, userRole, effectiveRole: contextEffectiveRole, toggleViewRole, currentTechnicianId, login, logout, isLoading: authLoading } = useAuth();
    const effectiveRole = contextEffectiveRole ?? userRole;

    // Domain Hooks
    const {
        orders, activeOrders, archivedOrders,
        createOrder, updateOrder, deleteOrder, deleteOrders, assignOrder,
        deleteEvent, updateEvent,
        loadMoreActiveOrders, loadMoreArchivedOrders,
        isFetchingOrders, isArchivedFetching,
        isLoading: isOrdersLoading,
        error: ordersError
    } = useOrdersDomain();

    const {
        clients,
        saveClient, createClientFromOrder, addWorkSiteToClient, deleteClient,
        loadMoreClients,
        isLoading: isClientsLoading,
        error: clientsError
    } = useClientsDomain();

    const {
        technicians,
        saveTechnician, deleteTechnician,
        isLoading: isTechniciansLoading,
        error: techniciansError
    } = useTechniciansDomain();

    const {
        companySettings,
        updateSettings,
        isLoading: isSettingsLoading,
        error: settingsError
    } = useSettingsDomain();

    const {
        isLoading: isStatsLoading,
        error: statsError
    } = useDashboardStatsDomain();

    // Consolidated loading and error states
    const isLoading = isOrdersLoading || isClientsLoading || isTechniciansLoading || isSettingsLoading || isStatsLoading;
    const initializationError = ordersError || clientsError || techniciansError || settingsError || statsError;

    const { showToast } = useToast();
    const { confirm } = useConfirm();
    const location = useLocation();
    const navigate = useNavigate();

    const handleToggleRole = () => {
        const goingTech = effectiveRole === 'admin';
        toggleViewRole?.();
        navigate(goingTech ? '/orders' : '/dashboard');
    };

    // Routes that bypass onboarding — computed early so they're available everywhere
    const isBypassPath = ['/', '/verified', '/privacy', '/terms', '/login', '/reset-password', '/join'].includes(location.pathname);

    // V2300: Refined flow detection. Only consider it an auth flow if we are on the specific page
    // or if we are NOT authenticated yet. This prevents loops after successful auth.
    const isRecoveryFlow = location.pathname === '/reset-password' ||
        (!isAuthenticated && (window.location.hash.includes('type=recovery') || window.location.search.includes('type=recovery')));

    const isSignupFlow = location.pathname === '/verified' ||
        (!isAuthenticated && (window.location.hash.includes('type=signup') || window.location.search.includes('type=signup')));

    const isAuthFlow = isRecoveryFlow || isSignupFlow;


    const [dashboardFilters, setDashboardFilters] = useDashboardFilters();


    const [selectedTechOrder, setSelectedTechOrder] = useState<WorkOrder | null>(null);

    const { handleUpdateOrder, handleDashboardNavigate, handleNavigateToTechOrders } = useOrderHandlers({
        orders: orders || [],
        selectedOrder: selectedTechOrder,
        setSelectedOrder: setSelectedTechOrder,
        updateOrder,
        deleteOrders,
        userRole: effectiveRole,
        setDashboardFilters: (f) => setDashboardFilters(f)
    });
    const [isClientModalOpen, setIsClientModalOpen] = useState(false);
    const [editingClient, setEditingClient] = useState<Client | null>(null);
    const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
    const [invoiceClient, setInvoiceClient] = useState<Client | null>(null);

    // V2152: Stripe return polling — reactive to URL changes (v4.86 Native Fix)
    const [searchParams, setSearchParams] = useSearchParams();
    const checkoutSuccess = searchParams.get('checkout') === 'success';
    const checkoutCompanyId = searchParams.get('company_id') || '';

    const [isPollingPayment, setIsPollingPayment] = useState(false);
    const [pollingTimeout, setPollingTimeout] = useState(false);

    useEffect(() => {
        if (!checkoutSuccess || !checkoutCompanyId) {
            setIsPollingPayment(false);
            return;
        }

        setIsPollingPayment(true);

        // FIX: Use a ref-captured flag to avoid updating state on an unmounted component
        let isMounted = true;
        let attempts = 0;
        const maxAttempts = 15; // 15 × 2s = 30s max
        const interval: ReturnType<typeof setInterval> = setInterval(async () => {
            if (!isMounted) return;
            attempts++;
            try {
                const { data, error: fetchError } = await supabase
                    .from('companies')
                    .select('subscription_status')
                    .eq('id', checkoutCompanyId)
                    .single();

                if (!isMounted) return;

                if (data?.subscription_status === 'active' || data?.subscription_status === 'trialing') {
                    clearInterval(interval);
                    setIsPollingPayment(false);
                    // Clear params and reload to refresh all data contexts
                    setSearchParams({});
                    window.location.reload();
                    return;
                }
                if (fetchError) console.error("Polling error:", fetchError);
            } catch (err) { console.error("Polling catch:", err); }

            if (isMounted && attempts >= maxAttempts) {
                clearInterval(interval);
                setIsPollingPayment(false);
                setPollingTimeout(true);
            }
        }, 2000);

        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, [checkoutSuccess, checkoutCompanyId, setSearchParams]);

    // Deep Link handler: parte-diario:// URLs from emails/invitations
    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return;
        let listener: any;
        (async () => {
            listener = await CapacitorApp.addListener('appUrlOpen', ({ url }) => {
                console.log('[DeepLink] App opened with URL:', url);
                if (!url) return;

                let parsed: URL;
                try {
                    // Normalize custom scheme to something URL constructor can handle reliably
                    const normalized = url.startsWith('parte-diario://')
                        ? url.replace('parte-diario://', 'https://app/')
                        : url;
                    parsed = new URL(normalized);
                } catch { return; }

                const path = parsed.pathname;
                const search = parsed.search;

                if (path === '/reset-password' || search.includes('type=recovery')) {
                    navigate('/reset-password');
                } else if (path === '/join' || (path === '/callback' && search.includes('token='))) {
                    navigate('/join' + search);
                } else if (path === '/verified' || search.includes('type=signup')) {
                    navigate('/verified' + search);
                } else if (path.startsWith('/orders/')) {
                    navigate(path);
                } else if (search.includes('checkout=success')) {
                    // V2182: Handle Stripe success redirect back to app
                    navigate('/' + search);
                } else if (search.includes('onboarding=true')) {
                    navigate('/' + search);
                }
            });
        })();
        return () => { listener?.remove?.(); };
    }, [navigate]);

    // V2199: appStateChange listener — fallback for when deep link fails (e.g. Stripe redirects via Safari)
    // When the user returns to the foreground manually after paying, we check localStorage for a pending
    // onboarding step. If found, we reload to force App.tsx to re-evaluate needsOnboardingLocal.
    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return;
        let listener: any;
        (async () => {
            listener = await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
                if (!isActive) return; // only care about foreground
                const pendingStep = localStorage.getItem('onboarding_step');
                if (pendingStep === 'invite' || pendingStep === 'plan') {
                    console.log('[AppState] Returning from background with pending onboarding step:', pendingStep);
                    // Force a re-render so needsOnboardingLocal picks up the localStorage flag
                    window.location.reload();
                }
            });
        })();
        return () => { listener?.remove?.(); };
    }, []);

    // Technician-specific data
    const currentTech = useMemo(() => effectiveRole === 'technician' && currentTechnicianId
        ? (technicians || []).find(t => t.id === currentTechnicianId)
        : null, [effectiveRole, currentTechnicianId, technicians]);


    const safeSettings = companySettings || { name: 'Parte Diario', notifications: { adminEmail: '', adminPhone: '', materialChannels: { email: false, whatsapp: false, sms: false }, issueChannels: { email: false, whatsapp: false, sms: false } } };

    const {
        handleLogin,
        handleDeleteOrder,
        handleDeleteOrders,
        handleGeneratePDF,
        handleRequestDeleteClient
    } = useAppHandlers({
        orders: orders || [],
        technicians: technicians || [],
        companySettings: safeSettings,
        deleteOrder,
        deleteClient,
        updateOrder,
        showToast,
        confirm,
        login: (role) => login(role)
    });

    const handleCreateOrder = async (newOrder: WorkOrder): Promise<boolean> => {
        try {
            await createOrder({ ...newOrder, createdByRole: effectiveRole ?? undefined });
            return true;
        } catch (error) {
            console.error('handleCreateOrder failed:', error);
            return false;
        }
    };

    const handleAssignOrder = (orderId: string, technicianId: string | null) => {
        assignOrder(orderId, technicianId);
    };

    const isOffline = (typeof window !== 'undefined' && (window as any).__TEST_FORCE_OFFLINE__ === true) ||
                      (typeof navigator !== 'undefined' && !navigator.onLine);

    // V2148: Centralized Initialization Check
    // If offline, don't wait for database queries (they are paused by React Query when offline)
    const isAppLoading = authLoading || (isAuthenticated && isLoading && !isOffline);

    // V41: Robust Onboarding Check (Refined v2149 + v4.84 Anti-Flicker)
    const needsOnboardingLocal = useMemo(() => {
        // Wait for auth to fully load before making any onboarding decisions
        // FIX: Guard against authLoading specifically, not isAppLoading (which includes DataContext)
        if (!isAuthenticated || authLoading) return false;

        // Technicians NEVER see the company onboarding screen
        if (userRole === 'technician') return false;

        // null role means we're still loading the profile - never trigger onboarding yet
        if (userRole === null) return false;

        // Admin onboarding check: if admin has no technician list loaded, or no currentTechnicianId,
        // or no company settings, they need to complete onboarding.
        if (userRole === 'admin') {
            // V2197: Forzar onboarding si acabamos de volver de Stripe (v4.87 Native Fix)
            if (checkoutSuccess) {
                console.log("App.tsx: needsOnboardingLocal = true (checkoutSuccess)");
                return true;
            }

            // If DataContext hasn't loaded yet, don't flash the onboarding screen
            if (isLoading) return false;

            const hasNoSettings = !safeSettings || !safeSettings.id || safeSettings.name === 'Parte Diario';

            // V2196: El onboarding se considera incompleto si la flag explícita no es true
            const storedStep = localStorage.getItem('onboarding_step');
            if (storedStep === 'invite' || storedStep === 'plan') {
                console.log("App.tsx: needsOnboardingLocal = true (storedStep)", storedStep);
                return true;
            }

            const isOnboardingCompleted = safeSettings?.onboardingCompleted === true;
            const hasNoTechnicians = technicians.length === 0;
            const hasOnlyAdmin = technicians.length === 1 && technicians.some(t => t.id === currentTechnicianId);

            if (!isOnboardingCompleted && (hasNoTechnicians || hasOnlyAdmin)) {
                console.log("App.tsx: needsOnboardingLocal = true (!isOnboardingCompleted)", { hasNoTechnicians, hasOnlyAdmin });
                return true;
            }

            if (hasNoSettings || !currentTechnicianId) {
                console.log("App.tsx: needsOnboardingLocal = true (hasNoSettings/noCurrentTech)", { hasNoSettings, currentTechnicianId });
                return true;
            }

            // FIX: Removed dead code. The check `userRole !== 'admin'` was logically
            // impossible here since this entire block is already inside `if (userRole === 'admin')`.
            // The condition could never evaluate to true, so it was never executed.

            return false;
        }

        return false;
    }, [isAuthenticated, authLoading, isLoading, technicians, currentTechnicianId, userRole, safeSettings, checkoutSuccess]);

    // V65: Critical Error Screen
    if (initializationError && !isAppLoading && !isNetworkError(initializationError)) {
        return (
            <div className="flex flex-col items-center justify-center h-[100dvh] bg-white dark:bg-dark-input text-slate-900 dark:text-white p-6 transition-colors">
                <div className="bg-white dark:bg-dark-card p-8 rounded-2xl shadow-xl border border-red-500/20 dark:border-red-500/30 max-w-md w-full text-center transition-colors">
                    <h2 className="text-2xl font-bold mb-2">Error de Conexión</h2>
                    <p className="text-slate-600 dark:text-slate-400 mb-6">{initializationError.message}</p>
                    <button onClick={() => window.location.reload()} className="w-full py-3 bg-blue-600 rounded-xl font-bold text-white">Reintentar</button>
                    <button onClick={logout} className="mt-4 text-sm underline">Cerrar Sesión</button>
                </div>
            </div>
        );
    }

    // V2148: Global App Loading Implementation
    // This prevents flickering between routes while data is still coming in
    // EXCEPTION: Bypass paths (/verified, /login, etc) should never be blocked by the global loader
    if (isAppLoading && !isBypassPath) {
        console.log("App.tsx: Loading State (Blocked):", { authLoading, isAuthenticated, isLoading, isAppLoading });
        return (
            <div className="flex items-center justify-center h-[100dvh] bg-white dark:bg-dark-input text-slate-900 dark:text-white transition-colors">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-blue-600 dark:border-primary border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-sm font-bold animate-pulse">Iniciando aplicación...</p>
                </div>
            </div>
        );
    }

    // Show payment activation screen while webhook is being processed
    // V2196: Only show if we are NOT in the onboarding flow, to let OnboardingFlow handle its own success state.
    if (isPollingPayment && !needsOnboardingLocal) {
        return (
            <div className="flex flex-col items-center justify-center h-[100dvh] bg-white dark:bg-dark-input text-slate-900 dark:text-white gap-6 transition-colors p-6 text-center">
                {!pollingTimeout ? (
                    <>
                        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        <h2 className="text-xl font-bold">Activando tu suscripción...</h2>
                        <p className="text-sm text-slate-500">Esto solo tarda unos segundos.</p>
                    </>
                ) : (
                    <>
                        <div className="w-16 h-16 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mb-4">
                            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        </div>
                        <h2 className="text-xl font-bold">Stripe está tardando más de lo esperado</h2>
                        <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">Hemos recibido tu vuelta desde el pago, pero la confirmación oficial (webhook) aún no ha llegado a nuestros servidores. Si el pago se completó, se activará en breve.</p>
                        <div className="flex gap-4">
                            <button onClick={() => window.location.reload()} className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors">
                                Volver a comprobar
                            </button>
                            <button onClick={() => {
                                setIsPollingPayment(false);
                                navigate('/');
                            }} className="px-6 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
                                Ir a los planes
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    }

    if (needsOnboardingLocal && !isBypassPath && !isAuthFlow) {
        return (
            <React.Suspense fallback={<div className="h-screen bg-white dark:bg-dark-input transition-colors" />}>
                <OnboardingFlow />
            </React.Suspense>
        );
    }






    return (
        <React.Suspense fallback={
            <div className="flex items-center justify-center h-[100dvh] bg-white dark:bg-dark-input text-slate-900 dark:text-white transition-colors">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-blue-600 dark:border-primary border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-sm font-bold animate-pulse">Cargando...</p>
                </div>
            </div>
        }>

            <div className="min-h-full pt-safe pb-safe">
                <Routes>
                    {/* Public Routes - Priority */}
                    <Route path="/privacy" element={
                        <React.Suspense fallback={<div className="h-screen bg-white dark:bg-dark-bg transition-colors" />}>
                            <PrivacyPolicy />
                        </React.Suspense>
                    } />
                    <Route path="/terms" element={
                        <React.Suspense fallback={<div className="h-screen bg-white dark:bg-dark-bg transition-colors" />}>
                            <TermsAndConditions />
                        </React.Suspense>
                    } />
                    <Route path="/pricing" element={
                        <React.Suspense fallback={<div className="h-screen bg-white dark:bg-dark-bg transition-colors" />}>
                            <PricingPage />
                        </React.Suspense>
                    } />

                    <Route path="/login" element={
                        isAuthenticated ? (
                            effectiveRole === 'technician' ? <Navigate to="/orders" replace /> :
                            effectiveRole === 'admin' ? <Navigate to="/dashboard" replace /> :
                            <Navigate to="/" replace />
                        ) : (
                            <Login onLogin={handleLogin} />
                        )
                    } />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/join" element={
                        <React.Suspense fallback={<div className="h-screen bg-white dark:bg-dark-input transition-colors" />}>
                            <JoinCompany />
                        </React.Suspense>
                    } />
                    <Route path="/verified" element={<Verified />} />
                    <Route path="/auth/confirm" element={<Verified />} />

                    <Route path="/" element={
                        authLoading || (isAuthenticated && userRole === null) ? (
                            <div className="h-screen bg-white dark:bg-dark-bg transition-colors" />
                        ) : isAuthenticated ? (
                            userRole === 'technician' && !currentTechnicianId ? (
                                <Navigate to="/join" replace />
                            ) : (
                                <Navigate to={effectiveRole === 'technician' ? "/orders" : "/dashboard"} replace />
                            )
                        ) : isAuthFlow ? (
                            <Navigate to={isRecoveryFlow ? "/reset-password" : "/verified"} replace />
                        ) : Capacitor.isNativePlatform() ? (
                            <Navigate to="/login" replace />
                        ) : (
                            <React.Suspense fallback={<div className="h-screen bg-white dark:bg-dark-bg transition-colors" />}>
                                <LandingPage />
                            </React.Suspense>
                        )
                    } />

                    {/* Protected Routes */}
                    <Route element={<ProtectedLayout />}>

                        {/* No root route here as it's handled above */}

                        <Route path="/select-plan" element={
                            <React.Suspense fallback={<div className="h-screen bg-white dark:bg-dark-bg transition-colors" />}>
                                <div className="p-8 max-w-4xl mx-auto mt-16">
                                    <SelectPlan onNext={() => navigate('/dashboard')} companyId={safeSettings?.id || ''} />
                                </div>
                            </React.Suspense>
                        } />

                        <Route path="/invite" element={
                            <React.Suspense fallback={<div className="h-screen bg-white dark:bg-dark-bg transition-colors" />}>
                                <div className="p-8 max-w-4xl mx-auto mt-16">
                                    <InviteTechnicians onNext={() => navigate('/dashboard')} companyId={safeSettings?.id || ''} />
                                </div>
                            </React.Suspense>
                        } />


                        <Route path="/dashboard" element={
                            <Dashboard onNavigate={handleDashboardNavigate} />
                        } />

                        <Route path="/orders" element={
                            effectiveRole === 'technician' ? (
                                <TechnicianErrorBoundary onReset={() => window.location.reload()}>
                                    <TechViewWrapper
                                        orders={activeOrders}
                                        archivedOrders={archivedOrders}
                                        technicians={technicians || []}
                                        clients={clients || []}
                                        companySettings={safeSettings}
                                        onUpdateOrder={handleUpdateOrder}
                                        onCreateOrder={handleCreateOrder}
                                        onCreateClient={createClientFromOrder}
                                        onAddWorkSiteToClient={addWorkSiteToClient}
                                        onLogout={logout}
                                        onToggleRole={handleToggleRole}
                                    />
                                </TechnicianErrorBoundary>
                            ) : effectiveRole === 'admin' ? (
                                <WorkOrderList
                                    orders={activeOrders}
                                    technicians={technicians}
                                    clients={clients}
                                    onSelectOrder={(order: WorkOrder) => { setSelectedTechOrder(order); navigate(`/orders/${order.id}`); }}
                                    onCreateOrder={handleCreateOrder}
                                    onCreateClient={createClientFromOrder}
                                    onAssignOrder={handleAssignOrder}
                                    onUpdateOrder={handleUpdateOrder}
                                    onDeleteOrder={handleDeleteOrder}
                                    onDeleteOrders={handleDeleteOrders}
                                    onUnarchiveOrders={(ids: string[]) => {
                                        ids.forEach(id => {
                                            const order = orders.find(o => o.id === id);
                                            if (order) updateOrder({ ...order, isArchived: false });
                                        });
                                    }}
                                    onAddWorkSiteToClient={addWorkSiteToClient}
                                    activeFilters={dashboardFilters}
                                    onUpdateEvent={updateEvent}
                                    onDeleteEvent={deleteEvent}
                                    companySettings={companySettings}
                                />
                            ) : (
                                <div className="flex items-center justify-center h-[100dvh] bg-white dark:bg-dark-input transition-colors">
                                    <div className="w-12 h-12 border-4 border-blue-600 dark:border-primary border-t-transparent rounded-full animate-spin" />
                                </div>
                            )
                        } />

                        {/* DETAILS VIEW - Supports Deep Linking now */}
                        <Route path="/orders/:id" element={
                            effectiveRole === 'technician' ? (
                                <TechnicianErrorBoundary onReset={() => window.location.reload()}>
                                    <TechViewWrapper
                                        orders={activeOrders}
                                        archivedOrders={archivedOrders}
                                        technicians={technicians || []}
                                        clients={clients || []}
                                        companySettings={safeSettings}
                                        onUpdateOrder={handleUpdateOrder}
                                        onCreateOrder={handleCreateOrder}
                                        onCreateClient={createClientFromOrder}
                                        onAddWorkSiteToClient={addWorkSiteToClient}
                                        onLogout={logout}
                                        onDeleteOrder={handleDeleteOrder}
                                        onToggleRole={handleToggleRole}
                                    />
                                </TechnicianErrorBoundary>
                            ) : (
                                <TechViewWrapper
                                    orders={activeOrders}
                                    archivedOrders={archivedOrders}
                                    technicians={technicians}
                                    clients={clients}
                                    companySettings={safeSettings}
                                    onUpdateOrder={handleUpdateOrder}
                                    onCreateOrder={handleCreateOrder}
                                    onCreateClient={createClientFromOrder}
                                    onAddWorkSiteToClient={addWorkSiteToClient}
                                    onLogout={logout}
                                    onDeleteOrder={handleDeleteOrder}
                                    onToggleRole={handleToggleRole}
                                />
                            )
                        } />

                        <Route path="/archive" element={
                            <WorkOrderList
                                orders={effectiveRole === 'technician' ? activeOrders : archivedOrders}
                                technicians={technicians}
                                clients={clients}
                                onSelectOrder={(order: WorkOrder) => { setSelectedTechOrder(order); navigate(`/orders/${order.id}`); }}
                                onCreateOrder={() => { }}
                                onUpdateOrder={handleUpdateOrder}
                                onDeleteOrder={handleDeleteOrder}
                                onDeleteOrders={handleDeleteOrders}
                                onUnarchiveOrders={(orderIds: string[]) => {
                                    orderIds.forEach(id => {
                                        const order = (effectiveRole === 'technician' ? activeOrders : orders).find(o => o.id === id);
                                        if (order) {
                                            if (effectiveRole === 'technician') {
                                                updateOrder({ ...order, hiddenForTechnician: false });
                                            } else {
                                                updateOrder({ ...order, isArchived: false, status: WorkOrderStatus.COMPLETED });
                                            }
                                        }
                                    });
                                }}
                                onAddWorkSiteToClient={addWorkSiteToClient}
                                isArchiveView={true}
                                onDeleteEvent={deleteEvent}
                                onUpdateEvent={updateEvent}
                                companySettings={companySettings}
                                role={effectiveRole}
                            />
                        } />

                        <Route path="/reports" element={
                            <ReportsView
                                orders={activeOrders}
                                clients={clients}
                                technicians={technicians}
                                companySettings={companySettings}
                                onLoadMore={async () => {
                                    await loadMoreActiveOrders();
                                }}
                                isFetching={isFetchingOrders}
                            />
                        } />

                        <Route path="/clients" element={
                            <ClientManagement
                                clients={clients}
                                orders={orders}
                                onOpenClientModal={(client: Client) => {
                                    setEditingClient(client);
                                    setIsClientModalOpen(true);
                                }}
                                onOpenInvoiceModal={(client: Client) => {
                                    setInvoiceClient(client);
                                    setIsInvoiceModalOpen(true);
                                }}
                                onRequestDeleteClient={handleRequestDeleteClient}
                                onNavigateToOrders={(clientId: string) => {
                                    setDashboardFilters({ status: null, priority: null, tag: null, technician: null, dateRange: null, client: clientId });
                                    navigate('/orders');
                                }}
                                onLoadMore={loadMoreClients}
                            />
                        } />

                        <Route path="/technicians" element={
                            <TechnicianManagement
                                technicians={technicians}
                                onCreateTechnician={async (tech: any) => {
                                    try {
                                        await saveTechnician(tech as any);
                                        showToast('Técnico invitado correctamente', 'success');
                                    } catch (err: any) {
                                        // Error already handled by toast in hook, but we catch to avoid unhandled promise
                                    }
                                }}
                                onUpdateTechnician={async (tech: any) => await saveTechnician(tech, tech.id)}
                                onDeleteTechnician={async (id: string) => await deleteTechnician(id)}
                                orders={orders}
                                onSelectOrder={(order: WorkOrder) => { setSelectedTechOrder(order); navigate(`/orders/${order.id}`); }}
                                onNavigateToOrders={handleNavigateToTechOrders}
                            />
                        } />

                        <Route path="/settings" element={
                            <SettingsView
                                settings={companySettings}
                                onUpdate={updateSettings}
                                onLogout={logout}
                                isTechnicianView={effectiveRole === 'technician'}
                            />
                        } />

                        <Route path="/account" element={
                            <AccountView
                                settings={companySettings}
                                onUpdate={updateSettings}
                                isTechnicianView={effectiveRole === 'technician'}
                            />
                        } />

                        <Route path="/subscription" element={<SubscriptionView />} />
                        <Route path="/billing" element={<SubscriptionView />} />
                    </Route>

                    {/* Catch-all redirect to Landing or Dashboard */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>


                {/* Modals are Global */}
                <ClientModal
                    isOpen={isClientModalOpen}
                    client={editingClient}
                    onClose={() => setIsClientModalOpen(false)}
                    onSave={async (data: any) => {
                        await saveClient(data, data.workSites || [], editingClient);
                        setIsClientModalOpen(false);
                    }}
                />
                {/* Other Modals... (Invoice, Delete Confirmations) remain same structure */}
                <InvoiceModal
                    isOpen={isInvoiceModalOpen}
                    client={invoiceClient}
                    orders={orders}
                    onClose={() => setIsInvoiceModalOpen(false)}
                    onGeneratePDF={handleGeneratePDF}
                />

            </div>
        </React.Suspense>
    );
};





export default App;